import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import type { AppDispatch } from '@/store'
import { useAppSelector } from '@/store/hooks'
import { useNotifications } from '@/hooks/useNovuNotifications'
import { fetchPendingAnnouncements } from '@/store/slices/announcementsSlice'
import { publishAnnouncementEvent } from './announcementBroadcast'

/**
 * How many recent notifications the bridge watches. 5 was too tight: a user who was
 * offline while several notifications piled up would have the announcement pushed
 * off the window — no refetch, and (since this component is what marks them read)
 * a bell badge stuck on a number nobody can clear.
 */
const BRIDGE_WATCH_LIMIT = 10

/**
 * Tier 1 instant surfacing. MUST render inside <NovuProviderWrapper> (the Novu context).
 * Watches incoming Novu notifications; when an `announcement-published` arrives it triggers
 * a /pending refetch so the popup surfaces immediately instead of waiting for the poll.
 *
 * It also marks that Novu notification read on arrival. The announcement's real state lives
 * in main-api (`/pending` + the Announcements page behind <AnnouncementBell/>); the inbox
 * entry is only an arrival trace, deliberately stateless. Marking it read on arrival is what
 * keeps the bell badge from counting a copy the user can never act on — and it is why the
 * backend no longer reaches into Novu to write back reads or purge messages on delete.
 *
 * Guard: NovuProviderWrapper renders a Fallback subtree WITHOUT <NovuProvider> until the
 * Redux userId lands (novu-provider.tsx). Calling useNovu in that window throws, so we only
 * mount the hook-using inner component once userId exists (mirrors the wrapper's own gate).
 */
export function AnnouncementNovuBridge() {
  const userId = useAppSelector((state) => state.user.userId)
  if (!userId) return null
  return <AnnouncementNovuBridgeInner />
}

function AnnouncementNovuBridgeInner() {
  const dispatch = useDispatch<AppDispatch>()
  const { notifications } = useNotifications({ limit: BRIDGE_WATCH_LIMIT })
  const seen = useRef<Set<string>>(new Set())

  useEffect(() => {
    // 刷新每轮只做一次。这里曾经写在循环体里 —— 每命中一条通知就 dispatch 一次
    // 拉取、再 publish 一次 'refetch'(AnnouncementsHost 收到又 dispatch 一次),
    // 于是 N 条未读公告通知 = 2N 次一模一样的 GET /system/announcements/pending。
    // 实测单次进 /dashboard 打出 20 次(NOVAO-62)。/pending 是一次性的全量列表,
    // 拉 N 次拿到的是同一份东西。
    //
    // 标记已读仍然逐条做 —— 那个是 per-notification 的副作用,不能跟着一起去重。
    let arrived = false
    for (const n of notifications) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const an = n as any
      const wf: string | undefined = an.workflow?.identifier ?? an.templateIdentifier
      const id: string | undefined = an.id
      if (wf === 'announcement-published' && id && !seen.current.has(id)) {
        seen.current.add(id)
        // Best-effort: read() resolves a Result rather than throwing, so a failure just
        // leaves the badge up until the next mount (seen resets) retries it.
        if (!an.isRead) void an.read()
        arrived = true
      }
    }
    if (arrived) {
      void dispatch(fetchPendingAnnouncements())
      publishAnnouncementEvent({ kind: 'refetch' })
    }
  }, [notifications, dispatch])

  return null
}
